import { AuthContext } from '@modules/auth/contexts/auth-plus.context';
import { useContext, useState } from 'react';
import { FaKey, FaSpinner, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { SystemSettingService } from '../services/system-setting.service';

function VietGuysSetting() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const [refreshToken, setRefreshToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!refreshToken.trim()) {
      setError('Please enter a refresh token');
      return;
    }

    if (!auth) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await SystemSettingService.updateVietGuysToken(auth, setAuth, logout, refreshToken.trim());

      if (result) {
        setSuccess('✅ Refresh token updated successfully!');
        setRefreshToken('');
      } else {
        throw new Error('Failed to update refresh token');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update refresh token. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="d-flex align-items-center gap-3 mb-4">
              <FaKey size={40} className="text-primary" />
              <h1 className="display-4 fw-bold text-dark mb-0">VietGuys Settings</h1>
            </div>

            {/* Success Alert */}
            {success && (
              <div className="alert alert-success alert-dismissible fade show d-flex align-items-center gap-2" role="alert">
                <FaCheckCircle size={20} />
                <span>{success}</span>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccess(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show d-flex align-items-center gap-2" role="alert">
                <FaTimesCircle size={20} />
                <span><strong>Error:</strong> {error}</span>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {/* Loading Overlay */}
            {loading && (
              <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                <div className="text-center">
                  <FaSpinner className="text-white fa-spin" size={50} />
                  <p className="text-white mt-3 fs-5">Updating...</p>
                </div>
              </div>
            )}

            {/* Main Card */}
            <div className="card shadow-lg border-0">
              <div className="card-header text-white py-4"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <h4 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <FaKey />
                  Update VietGuys Refresh Token Manually
                </h4>
              </div>
              <div className="card-body p-5">
                <div className="mb-4">
                  <label htmlFor="refreshToken" className="form-label fw-semibold text-secondary">
                    Refresh Token
                  </label>
                  <input
                    id="refreshToken"
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Enter new refresh token..."
                    value={refreshToken}
                    onChange={(e) => setRefreshToken(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />
                  <div className="form-text">
                    Enter the new VietGuys refresh token to update the system configuration.
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading || !refreshToken.trim()}
                  className={`btn btn-lg w-100 fw-semibold ${refreshToken.trim() && !loading
                    ? 'btn-primary'
                    : 'btn-secondary'
                    }`}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="fa-spin me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaKey className="me-2" />
                      Update Token
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="card border-info mt-4">
              <div className="card-body">
                <p className="mb-0 text-muted small">
                  <strong>Note:</strong> This will update the VietGuys refresh token used for API authentication.
                  Make sure you have the correct token before updating. Press Enter to submit quickly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default VietGuysSetting;
